/**
 * NuevaAcademiaModal Component
 * 
 * Modal para crear/editar academias con los siguientes campos:
 * - Institución (selector)
 * - Nombre de Academia
 * - Código
 * - Descripción
 * - Estado (ACTIVO/INACTIVO)
 * 
 * Sigue exactamente el patrón de diseño de la captura image13:
 * - Header gris con título
 * - Campos de formulario organizados verticalmente
 * - Botones GUARDAR (azul) y CANCELAR (gris) centrados al final
 * 
 * Integración:
 * - Carga instituciones desde el API
 * - Valida campos requeridos
 * - Maneja tanto creación como edición
 */

import React, { useState, useEffect } from "react";
import styles from "../../styles/PantallaDeProgramasAcademicos.v2.module.css";
import { getInstitutions } from '../../api/institutions';

const defaultForm = {
  institucion: "",
  nombre: "",
  codigo: "",
  descripcion: "",
  estado: "ACTIVO",
};

export default function NuevaAcademiaModal({ open, onClose, onSubmit, initialData }) {
  const [form, setForm] = useState(defaultForm);
  const [institutions, setInstitutions] = useState([]);

  // Cargar instituciones cuando se abre el modal
  useEffect(() => {
    if (open) {
      getInstitutions().then(data => {
        console.log('[NuevaAcademiaModal] Instituciones cargadas:', data);
        setInstitutions(data || []);
      }).catch(err => {
        console.error('[NuevaAcademiaModal] Error al cargar instituciones:', err);
      });
    }
  }, [open]);

  // Cargar datos iniciales para edición
  useEffect(() => {
    if (open && initialData) {
      setForm({
        institucion: initialData.institucion || "",
        nombre: initialData.nombre || "",
        codigo: initialData.codigo || "",
        descripcion: initialData.descripcion || "",
        estado: initialData.estado || "ACTIVO",
      });
    } else if (open) {
      setForm(defaultForm);
    }
  }, [open, initialData]);

  // Cerrar modal con ESC
  useEffect(() => {
    if (!open) return;
    const handleEsc = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [open, onClose]);

  if (!open) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validación básica
    if (!form.nombre.trim()) {
      alert('El nombre de la academia es requerido');
      return;
    }
    if (!form.codigo.trim()) {
      alert('El código es requerido');
      return;
    }
    if (!form.institucion) {
      alert('Debe seleccionar una institución');
      return;
    }

    onSubmit?.(form);
    setForm(defaultForm);
    onClose?.();
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose?.();
    }
  };

  return (
    <div className={styles.modalBackdrop} onClick={handleBackdropClick}>
      <div className={styles.modalContainer} role="dialog" aria-modal="true" aria-labelledby="modal-title">
        {/* Header del modal - gris como en la captura */}
        <div 
          className={styles.modalHeader} 
          style={{ 
            background: '#d1d1d1', 
            padding: '18px 24px 12px', 
            borderTopLeftRadius: '8px', 
            borderTopRightRadius: '8px' 
          }}
        >
          <h2 
            id="modal-title"
            style={{ 
              fontWeight: 700, 
              fontSize: '20px', 
              margin: 0,
              color: '#333'
            }}
          >
            {initialData ? 'Editar Academia' : 'Nueva Sección Académica'}
          </h2>
        </div>

        {/* Formulario */}
        <form 
          className={styles.modalBody} 
          style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: '18px', 
            padding: '24px 32px 32px' 
          }} 
          onSubmit={handleSubmit}
        >
          {/* Institución */}
          <div className={styles.formGroup} style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <label className={styles.label} style={{ fontWeight: 500 }}>
              Institución *
            </label>
            <select 
              name="institucion" 
              value={form.institucion} 
              onChange={handleChange} 
              className={styles.input} 
              style={{ 
                height: '38px', 
                borderRadius: '6px', 
                border: '1px solid #ccc', 
                padding: '0 10px' 
              }} 
              required
            >
              <option value="">-- Selecciona una institución --</option>
              {institutions.map(inst => (
                <option key={inst.id} value={inst.id}>{inst.name}</option>
              ))}
            </select>
          </div>

          {/* Nombre de Academia */}
          <div className={styles.formGroup} style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <label className={styles.label} style={{ fontWeight: 500 }}>
              Nombre de Academia *
            </label>
            <input 
              name="nombre" 
              value={form.nombre} 
              onChange={handleChange} 
              className={styles.input} 
              style={{ 
                height: '38px', 
                borderRadius: '6px', 
                border: '1px solid #ccc', 
                padding: '0 10px' 
              }}
              placeholder="Ej: Humanidades, Ingenierías, etc."
              required
            />
          </div>

          {/* Código */}
          <div className={styles.formGroup} style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <label className={styles.label} style={{ fontWeight: 500 }}>
              Código *
            </label>
            <input 
              name="codigo" 
              value={form.codigo} 
              onChange={handleChange} 
              className={styles.input} 
              style={{ 
                height: '38px', 
                borderRadius: '6px', 
                border: '1px solid #ccc', 
                padding: '0 10px' 
              }}
              placeholder="Ej: HUM, ING, ART"
              required
            />
          </div>

          {/* Descripción */}
          <div className={styles.formGroup} style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <label className={styles.label} style={{ fontWeight: 500 }}>
              Descripción
            </label>
            <textarea 
              name="descripcion" 
              value={form.descripcion} 
              onChange={handleChange} 
              className={styles.input} 
              style={{ 
                minHeight: '80px', 
                borderRadius: '6px', 
                border: '1px solid #ccc', 
                padding: '8px 10px',
                resize: 'vertical'
              }}
              placeholder="Descripción opcional de la academia"
            />
          </div>

          {/* Estado */}
          <div className={styles.formGroup} style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <label className={styles.label} style={{ fontWeight: 500 }}>
              Estado
            </label>
            <select 
              name="estado" 
              value={form.estado} 
              onChange={handleChange} 
              className={styles.input} 
              style={{ 
                height: '38px', 
                borderRadius: '6px', 
                border: '1px solid #ccc', 
                background: form.estado === 'ACTIVO' ? '#e8f5e9' : '#ffebee', 
                color: form.estado === 'ACTIVO' ? '#2e7d32' : '#c62828', 
                fontWeight: 700, 
                padding: '0 10px' 
              }}
            >
              <option value="ACTIVO">ACTIVO</option>
              <option value="INACTIVO">INACTIVO</option>
            </select>
          </div>

          {/* Botones de acción - centrados como en la captura */}
          <div style={{ 
            display: 'flex', 
            gap: '18px', 
            marginTop: '18px', 
            justifyContent: 'center' 
          }}>
            <button 
              type="submit" 
              style={{ 
                background: '#1a3764', 
                color: '#fff', 
                fontWeight: 700, 
                border: 'none', 
                borderRadius: '6px', 
                padding: '10px 38px', 
                fontSize: '16px', 
                boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                cursor: 'pointer'
              }}
            >
              GUARDAR
            </button>
            <button 
              type="button" 
              style={{ 
                background: '#b7b7b7', 
                color: '#fff', 
                fontWeight: 700, 
                border: 'none', 
                borderRadius: '6px', 
                padding: '10px 38px', 
                fontSize: '16px', 
                boxShadow: '0 2px 6px rgba(0,0,0,0.10)',
                cursor: 'pointer'
              }} 
              onClick={onClose}
            >
              CANCELAR
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
